/**
 * Komal OS - CRM: Accounts list.
 *
 * Owner-scoped server-side (see app/routes/crm_routes.py's _owner_scope) -
 * a non-CEO Sales Department user only ever gets their own accounts back,
 * so the filters here are about narrowing further, not about visibility.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Building2, X, Upload, Mail, Copy, Loader2, AlertTriangle, CheckSquare, Square,
  Hand, CalendarClock, FileText, HeartHandshake, RotateCw, Languages, Download, MapPin, SlidersHorizontal, Send,
} from 'lucide-react';
import {
  listAccounts, createAccount, listTeam, listEvents, listEmailTypes, draftIntroEmails,
  exportAccountsCsv, backfillAccountLocations, getAccountIds, getAccountFilterOptions, listProducts,
} from '../../services/crmService';
import { downloadBlob } from '../../utils/downloadFile';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';

const emptyForm = () => ({ name: '', country: '', city: '', website: '', industry_notes: '' });

// Icon + short description per type - the backend is the source of truth
// for the key/label (see listEmailTypes), this just dresses up the
// picker; a type the backend adds later still works, just without a
// custom icon/description until this map is updated.
const TYPE_META = {
  introductory: { icon: Hand, desc: 'First outreach — no deal yet' },
  meeting_request: { icon: CalendarClock, desc: 'Propose a call or site visit' },
  quote_follow_up: { icon: FileText, desc: 'Nudge on a quote already sent' },
  thank_you: { icon: HeartHandshake, desc: 'After a call or meeting' },
  re_engagement: { icon: RotateCw, desc: 'Reconnect — gone quiet a while' },
};

function EmailTypePicker({ emailTypes, count, onGenerate, onClose, drafting }) {
  const [type, setType] = useState('introductory');
  const [useLocal, setUseLocal] = useState(true);

  return (
    <div className="glass-card p-5 mb-5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-800">What kind of email?</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      <p className="text-xs text-gray-500 mb-4">Applies to all {count} selected customer{count > 1 ? 's' : ''}</p>

      <div className="grid sm:grid-cols-2 gap-2 mb-4">
        {emailTypes.map((t) => {
          const meta = TYPE_META[t.key] || {};
          const Icon = meta.icon || Mail;
          const selected = type === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setType(t.key)}
              className={`text-left border rounded-xl px-3.5 py-3 ${selected ? 'border-komal-blue bg-komal-blue/5' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
            >
              <Icon size={16} className={selected ? 'text-komal-blue' : 'text-gray-500'} />
              <div className={`text-sm font-semibold mt-1 ${selected ? 'text-komal-blue' : 'text-gray-800'}`}>{t.label}</div>
              {meta.desc && <div className="text-[11.5px] text-gray-500 mt-0.5">{meta.desc}</div>}
            </button>
          );
        })}
      </div>

      <label className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 mb-4 cursor-pointer">
        <div className="flex items-center gap-2">
          <Languages size={16} className="text-gray-500 shrink-0" />
          <div>
            <div className="text-xs font-semibold text-gray-800">Local-language salutation</div>
            <div className="text-[11px] text-gray-500">Opens/closes in the recipient's language when known (e.g. "Guten Tag" for Germany) — body stays in English</div>
          </div>
        </div>
        <input type="checkbox" checked={useLocal} onChange={(e) => setUseLocal(e.target.checked)} className="w-4 h-4 accent-komal-blue shrink-0" />
      </label>

      <button
        onClick={() => onGenerate(type, useLocal)}
        disabled={drafting}
        className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-komal-blue rounded-lg px-3 py-2.5 disabled:opacity-60"
      >
        {drafting ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
        {drafting ? 'Drafting…' : `Generate ${count} draft${count > 1 ? 's' : ''}`}
      </button>
    </div>
  );
}

function DraftEmailsPanel({ drafts, onClose }) {
  const { showToast } = useUIStore();
  const [edited, setEdited] = useState(() =>
    Object.fromEntries(drafts.filter((d) => !d.error).map((d) => [d.account_id, { subject: d.subject, body: d.body }]))
  );

  const copyDraft = async (d) => {
    const text = `${edited[d.account_id].subject}\n\n${edited[d.account_id].body}`;
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied — paste into your email client', 'success');
    } catch {
      showToast('Could not copy — select and copy manually', 'error');
    }
  };

  return (
    <div className="glass-card p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Mail size={15} /> Drafted emails — review, edit, then copy into your own email client</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      <div className="space-y-4">
        {drafts.map((d) => (
          <div key={d.account_id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm">
                <span className="font-semibold text-gray-800">{d.account_name || `Customer #${d.account_id}`}</span>
                {d.contact_name && <span className="text-gray-500"> → {d.contact_name}{d.contact_email ? ` (${d.contact_email})` : ''}</span>}
                {!d.contact_name && !d.error && <span className="text-gray-400"> → no named contact on file</span>}
                {d.language && d.language !== 'English' && (
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{d.language} salutation</span>
                )}
              </div>
              {!d.error && (
                <button onClick={() => copyDraft(d)} className="flex items-center gap-1 text-xs font-semibold text-komal-blue border border-gray-300 rounded px-2 py-1 hover:bg-gray-50">
                  <Copy size={12} /> Copy
                </button>
              )}
            </div>
            {d.error ? (
              <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                <AlertTriangle size={12} /> {d.error}
              </div>
            ) : (
              <div className="space-y-1.5">
                <input
                  value={edited[d.account_id]?.subject || ''}
                  onChange={(e) => setEdited((prev) => ({ ...prev, [d.account_id]: { ...prev[d.account_id], subject: e.target.value } }))}
                  className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm font-medium"
                />
                <textarea
                  value={edited[d.account_id]?.body || ''}
                  onChange={(e) => setEdited((prev) => ({ ...prev, [d.account_id]: { ...prev[d.account_id], body: e.target.value } }))}
                  rows={6}
                  className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AccountsListPage() {
  const { user } = useAuthStore();
  const { showToast } = useUIStore();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [events, setEvents] = useState([]);

  const [q, setQ] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [eventId, setEventId] = useState('');
  // "" | country | city | any - which accounts are missing a location. Can arrive
  // pre-set from a link (e.g. the dashboard's "no country yet" note).
  const [searchParams] = useSearchParams();
  const [missing, setMissing] = useState(searchParams.get('missing') || '');
  const navigate = useNavigate();
  // The other criteria for picking who to email (2026-09-19) - same filters the CSV export uses.
  const EMPTY_ADV = { country: '', region: '', state: '', city: '', product_id: '', stage: '', quiet_days: '', has_email: '' };
  const [adv, setAdv] = useState(EMPTY_ADV);
  const [showAdv, setShowAdv] = useState(false);
  const [options, setOptions] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectingAll, setSelectingAll] = useState(false);
  const activeAdv = Object.values(adv).filter(Boolean).length;
  const buildFilters = () => ({
    q: q || undefined,
    owner_id: ownerId || undefined,
    event_id: eventId || undefined,
    missing: missing || undefined,
    ...Object.fromEntries(Object.entries(adv).map(([k, v]) => [k, v === '' ? undefined : v])),
  });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(() => new Set());
  const [emailTypes, setEmailTypes] = useState([]);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listAccounts(buildFilters());
      setAccounts(res.data.accounts);
      setSelected(new Set()); // a fresh result set invalidates row selection
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not load customers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listTeam().then((res) => setTeam(res.data.team)).catch(() => {});
    listEvents().then((res) => setEvents(res.data.events)).catch(() => {});
    listEmailTypes().then((res) => setEmailTypes(res.data.types)).catch(() => {});
    getAccountFilterOptions().then((res) => setOptions(res.data)).catch(() => {});
    listProducts().then((res) => setProducts(res.data.products || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(load, 300); // debounce the search box
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, ownerId, eventId, missing, adv]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('Customer name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await createAccount(form);
      showToast('Customer created', 'success');
      setCreating(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not create customer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = accounts.length > 0 && selected.size === accounts.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(accounts.map((a) => a.id)));

  // CEO-only (button is hidden for everyone else; the server enforces it too).
  // Exports whatever the current filters show - but with no 200-row cap.
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportAccountsCsv(buildFilters());
      downloadBlob(res.data, `komal-crm-accounts-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      // A blob response body has to be read as text to reach the server's message.
      let detail = null;
      try { detail = JSON.parse(await err.response.data.text()).detail; } catch { /* not JSON */ }
      showToast(detail || 'Could not export customers', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm('Fill in missing country, state and city on existing customers? It uses saved card addresses, phone numbers, and the city/state names already on each customer (and moves a city or state typed into the Country box to the right field). Anything already filled in is left alone.')) return;
    setBackfilling(true);
    try {
      const { data } = await backfillAccountLocations();
      const parts = [`${data.countries_filled} countries and ${data.cities_filled} cities filled in`];
      if (data.misplaced_fixed > 0) parts.push(`${data.misplaced_fixed} had a city or state typed in the Country box and ${data.misplaced_fixed === 1 ? 'was' : 'were'} corrected`);
      const more = data.still_unparsed + data.country_choices_left;
      if (data.rate_limited) parts.push('AI is busy — run again in a minute');
      else if (more > 0) parts.push(`${more} more to go — run it again`);
      if (data.no_country_left > 0) parts.push(`${data.no_country_left} still have no clear country — use the Location filter to find them`);
      showToast(parts.join(' · '), 'success', 8000);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not fill in locations', 'error');
    } finally {
      setBackfilling(false);
    }
  };

  // The list shows at most 200 rows; this fetches every id matching the filters.
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const res = await getAccountIds(buildFilters());
      setSelected(new Set(res.data.ids));
      if (res.data.truncated) showToast(`Selected the first ${res.data.count} — narrow the filters to reach the rest`, 'error');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not select all matching customers', 'error');
    } finally {
      setSelectingAll(false);
    }
  };

  const startBulkEmail = () => {
    const ids = [...selected];
    try { sessionStorage.setItem('crmBulkEmailIds', JSON.stringify(ids)); } catch { /* private mode - router state still carries it */ }
    navigate('/crm/bulk-email', { state: { ids } });
  };

  const handleGenerateDrafts = async (emailType, useLocalSalutation) => {
    setDrafting(true);
    try {
      const res = await draftIntroEmails([...selected], emailType, useLocalSalutation);
      setDrafts(res.data.drafts);
      setShowTypePicker(false);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not draft emails', 'error');
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">Customers</h1>
            <p className="text-sm text-white/70 mt-0.5">
              {user?.role === 'CEO' ? 'Every customer across the team.' : 'Customers you own.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'CEO' && (
              <>
                <button
                  onClick={handleBackfill}
                  disabled={backfilling}
                  title="Fill in missing country / state / city from saved card addresses"
                  className="flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3.5 py-2 disabled:opacity-60"
                >
                  {backfilling ? <Loader2 size={15} className="animate-spin" /> : <MapPin size={15} />} Fill locations
                </button>
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  title="Download every matching customer and contact as CSV"
                  className="flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3.5 py-2 disabled:opacity-60"
                >
                  {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV
                </button>
              </>
            )}
            <Link
              to="/crm/accounts/import"
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3.5 py-2"
            >
              <Upload size={15} /> Import CSV
            </Link>
            <button
              onClick={() => setCreating((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-komal-blue rounded-lg px-3.5 py-2"
            >
              <Plus size={15} /> New customer
            </button>
          </div>
        </div>

        {creating && (
          <form onSubmit={handleCreate} className="glass-card p-5 mb-5 grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">New customer</span>
              <button type="button" onClick={() => setCreating(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <input required placeholder="Company name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Industry notes" value={form.industry_notes} onChange={(e) => setForm({ ...form, industry_notes: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <button disabled={saving} className="sm:col-span-2 text-sm font-semibold text-white bg-green-700 rounded px-3 py-2 disabled:opacity-60">
              {saving ? 'Saving…' : 'Create customer'}
            </button>
          </form>
        )}

        {showTypePicker && selected.size > 0 && (
          <EmailTypePicker
            emailTypes={emailTypes}
            count={selected.size}
            drafting={drafting}
            onGenerate={handleGenerateDrafts}
            onClose={() => setShowTypePicker(false)}
          />
        )}

        {drafts && <DraftEmailsPanel drafts={drafts} onClose={() => setDrafts(null)} />}

        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:bg-white focus:text-gray-800 focus:placeholder-gray-400"
            />
          </div>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="text-sm rounded-lg border border-white/20 bg-white/10 text-white px-3 py-2">
            <option value="" className="text-gray-800">Owner: All</option>
            {team.map((t) => <option key={t.id} value={t.id} className="text-gray-800">{t.name}</option>)}
          </select>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="text-sm rounded-lg border border-white/20 bg-white/10 text-white px-3 py-2">
            <option value="" className="text-gray-800">Event: All</option>
            {events.map((ev) => <option key={ev.id} value={ev.id} className="text-gray-800">{ev.name}</option>)}
          </select>
          <select
            value={missing}
            onChange={(e) => setMissing(e.target.value)}
            className={`text-sm rounded-lg border px-3 py-2 ${missing ? 'border-amber-300 bg-amber-100 text-amber-900' : 'border-white/20 bg-white/10 text-white'}`}
          >
            <option value="" className="text-gray-800">Location: All</option>
            <option value="any" className="text-gray-800">Missing country or city</option>
            <option value="country" className="text-gray-800">Missing country</option>
            <option value="city" className="text-gray-800">Missing city</option>
          </select>
          <button
            type="button"
            onClick={() => setShowAdv((v) => !v)}
            className={`flex items-center gap-1.5 text-sm rounded-lg border px-3 py-2 ${activeAdv ? 'border-amber-300 bg-amber-100 text-amber-900' : 'border-white/20 bg-white/10 text-white'}`}
          >
            <SlidersHorizontal size={14} /> More filters{activeAdv ? ` (${activeAdv})` : ''}
          </button>

          {selected.size > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => { setDrafts(null); setShowTypePicker((v) => !v); }}
                disabled={selected.size > 15}
                title={selected.size > 15 ? 'One-off drafting is limited to 15 customers - use Bulk invite for more' : undefined}
                className="flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3.5 py-2 disabled:opacity-40"
              >
                <Mail size={14} />
                Draft email{selected.size > 1 ? 's' : ''} ({selected.size})
              </button>
              <button
                onClick={startBulkEmail}
                className="flex items-center gap-1.5 text-sm font-semibold text-white bg-komal-blue rounded-lg px-3.5 py-2"
              >
                <Send size={14} /> Bulk invite ({selected.size})
              </button>
            </div>
          )}
        </div>

        {showAdv && (
          <div className="glass-card p-4 mb-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ['region', 'Region', <><option value="">All regions</option>{(options?.regions || []).map((r) => <option key={r.region} value={r.region}>{r.region} ({r.count})</option>)}</>],
                ['country', 'Country', <><option value="">All countries</option>{(options?.countries || []).map((c) => <option key={c.code} value={c.country}>{c.country} ({c.count})</option>)}</>],
                ['product_id', 'Quoted product', <><option value="">Any product</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</>],
                ['stage', 'Deal stage', <><option value="">Any stage</option><option value="none">No deal yet</option>{['new', 'contacted', 'qualified', 'quoted', 'won', 'lost'].map((st) => <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>)}</>],
                ['quiet_days', 'Not contacted in', <><option value="">Any time</option>{[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}</>],
                ['has_email', 'Has an email', <><option value="">Either</option><option value="true">Yes</option><option value="false">No</option></>],
              ].map(([key, label, opts]) => (
                <label key={key} className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
                  <select value={adv[key]} onChange={(e) => setAdv({ ...adv, [key]: e.target.value })} className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white">{opts}</select>
                </label>
              ))}
              {[['state', 'State / region', 'adv-states', options?.states], ['city', 'City', 'adv-cities', options?.cities]].map(([key, label, listId, values]) => (
                <label key={key} className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
                  <input list={listId} value={adv[key]} onChange={(e) => setAdv({ ...adv, [key]: e.target.value })} placeholder="Any" className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  <datalist id={listId}>{(values || []).map((v) => <option key={v} value={v} />)}</datalist>
                </label>
              ))}
            </div>
            {activeAdv > 0 && (
              <button onClick={() => setAdv(EMPTY_ADV)} className="mt-3 text-xs font-semibold text-komal-blue hover:underline">Clear these filters</button>
            )}
          </div>
        )}

        {allSelected && accounts.length >= 200 && (
          <div className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5 mb-4 text-sm text-amber-900">
            <span>All {selected.size} customers on this page are selected — the list shows at most 200.</span>
            <button onClick={selectAllMatching} disabled={selectingAll} className="font-semibold underline disabled:opacity-60">
              {selectingAll ? 'Selecting…' : 'Select everyone matching these filters'}
            </button>
          </div>
        )}
        {selected.size > 200 && (
          <div className="flex items-center gap-3 bg-komal-blue/10 border border-white/20 rounded-lg px-3.5 py-2.5 mb-4 text-sm text-white">
            <span>{selected.size} customers selected (everyone matching the filters).</span>
            <button onClick={() => setSelected(new Set())} className="font-semibold underline">Clear selection</button>
          </div>
        )}

        <div className="glass-card overflow-x-auto">
          {loading ? (
            <p className="text-gray-500 text-sm p-5">Loading…</p>
          ) : accounts.length === 0 ? (
            <p className="text-gray-400 text-sm p-5 flex items-center gap-2"><Building2 size={15} /> No customers yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2.5 w-8">
                    <button onClick={toggleAll} className="text-gray-400 hover:text-komal-blue flex items-center" title="Select all">
                      {allSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                    </button>
                  </th>
                  <th className="px-4 py-2.5 font-semibold">Customer</th>
                  <th className="px-4 py-2.5 font-semibold">Client(s)</th>
                  <th className="px-4 py-2.5 font-semibold">Location</th>
                  <th className="px-4 py-2.5 font-semibold">Products quoted</th>
                  <th className="px-4 py-2.5 font-semibold">Owner</th>
                  <th className="px-4 py-2.5 font-semibold">Open deals</th>
                  <th className="px-4 py-2.5 font-semibold">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {accounts.map((a) => (
                  <tr key={a.id} className={`hover:bg-gray-50 ${selected.has(a.id) ? 'bg-komal-blue/5' : ''}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleRow(a.id)} className="text-gray-400 hover:text-komal-blue flex items-center">
                        {selected.has(a.id) ? <CheckSquare size={15} className="text-komal-blue" /> : <Square size={15} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/crm/accounts/${a.id}`} className="font-semibold text-komal-blue hover:underline">{a.name}</Link>
                      {a.website && <div className="text-xs text-gray-400">{a.website}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {a.contact_names.length === 0 ? (
                        <span className="text-gray-300">—</span>
                      ) : a.contact_names.length <= 2 ? (
                        a.contact_names.join(', ')
                      ) : (
                        `${a.contact_names.slice(0, 2).join(', ')} +${a.contact_names.length - 2} more`
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {a.country ? (
                        <>
                          {a.country}
                          {!a.country_code && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title="This isn't a country we recognise - edit the customer to fix it">check</span>}
                        </>
                      ) : (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">No country</span>
                      )}
                      <div className="text-xs mt-0.5">
                        {a.city || a.state
                          ? <span className="text-gray-400">{[a.city, a.state].filter(Boolean).join(', ')}</span>
                          : <span className="text-amber-600">No city</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {a.products.length === 0 ? <span className="text-gray-300">—</span> : a.products.map((p) => (
                          <span key={p.id} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{p.name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{a.owner?.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{a.open_deals} / {a.total_deals}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {a.last_activity_at ? new Date(a.last_activity_at).toLocaleDateString() : 'No activity yet'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
